import CardCurrency from "@/components/shared/CardCurrency/CardCurrency";
import LayoutInside from "@/layout/LayoutInside";
import {
  convertToSellsFilterParams,
  convertToVisualParams,
  FilterBadgeLabel,
  FilterParams,
  KeyLabel,
  normalizeParams,
} from "@/hooks/useSales";

import CustomSheet from "@/components/shared/Sheet/Sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import DateRangePicker from "@/pages/Dashboard/Partials/DateRangePicker";
import HourlySalesChart from "@/pages/Dashboard/Partials/HourlySalesChart";
import { ReportsService } from "@/services/reports.service";
import useCurrencyStore from '@/store/useCurrencyStore';
import useModalStore from "@/store/useModalStore";
import { STATUS_COLORS } from "@/utils/constants";
import { hasActiveFiltersBeyondDates } from "@/utils/convertFilters";
import { formatDateForAPI, getDefaultDate } from "@/utils/dateHelpers";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowLeft, ListFilter } from "lucide-react";
import {
  parseAsArrayOf,
  parseAsIsoDateTime,
  parseAsString,
  useQueryState,
} from "nuqs";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useCallback, useMemo } from "react";
import { DateRange } from "react-day-picker";
import FilterSalesByStatus from "./Partials/FilterSalesByStatus";
import { SalesByStatusChart } from "./Partials/SalesByStatusChart";

export default function SalesByStatus() {
  const { t } = useTranslation(['common']);

  const [fromDate, setFromDate] = useQueryState(
    'from',
    parseAsIsoDateTime.withDefault(getDefaultDate())
  );
  const [toDate, setToDate] = useQueryState(
    'to',
    parseAsIsoDateTime.withDefault(getDefaultDate())
  );

  const currency = useCurrencyStore((s) => s.currency);

  const ALL_STATUS = [
    'paid',
    'awaiting_payment',
    'not_authorized',
    'chargeback',
    'refunded',
  ] as const;

  const dateRange = useMemo(
    (): DateRange => ({
      from: fromDate,
      to: toDate,
    }),
    [fromDate, toDate]
  );

  const handleDateRangeChange = useCallback(
    (range: DateRange | undefined) => {
      if (range?.from) {
        setFromDate(range.from);
      }
      if (range?.to) {
        setToDate(range.to);
      }
    },
    [setFromDate, setToDate]
  );

  const [statusFilter, setStatusFilter] = useQueryState(
    'status',
    parseAsArrayOf(parseAsString)
  );
  const [paymentMethodsFilter, setPaymentMethodsFilter] = useQueryState(
    'payment_methods',
    parseAsArrayOf(parseAsString)
  );
  const [srcFilter, setSrcFilter] = useQueryState('src', parseAsString);
  const [utmCampaignFilter, setUtmCampaignFilter] = useQueryState(
    'utm_campaign',
    parseAsString
  );
  const [utmSourceFilter, setUtmSourceFilter] = useQueryState(
    'utm_source',
    parseAsString
  );
  const [utmMediumFilter, setUtmMediumFilter] = useQueryState(
    'utm_medium',
    parseAsString
  );
  const [utmContentFilter, setUtmContentFilter] = useQueryState(
    'utm_content',
    parseAsString
  );
  const [utmTermFilter, setUtmTermFilter] = useQueryState(
    'utm_term',
    parseAsString
  );
  const [offersFilter, setOffersFilter] = useQueryState(
    'offers',
    parseAsArrayOf(parseAsString)
  );
  const [productsFilter, setProductsFilter] = useQueryState(
    'products',
    parseAsArrayOf(parseAsString)
  );
  const [affiliatesFilter, setAffiliatesFilter] = useQueryState(
    'affiliates',
    parseAsArrayOf(parseAsString)
  );
  const [coproducersFilter, setCoproducersFilter] = useQueryState(
    'coproducers',
    parseAsArrayOf(parseAsString)
  );
  const [templatesFilter, setTemplatesFilter] = useQueryState(
    'templates',
    parseAsArrayOf(parseAsString)
  );

  const { modals, closeModal, openModal } = useModalStore();

  const filterParams = useMemo((): FilterParams => {
    const params: FilterParams = {};
    if (statusFilter?.length) params.status = statusFilter;
    if (paymentMethodsFilter?.length)
      params.payment_methods = paymentMethodsFilter;
    if (srcFilter) params.src = srcFilter;
    if (utmCampaignFilter) params.utm_campaign = utmCampaignFilter;
    if (utmSourceFilter) params.utm_source = utmSourceFilter;
    if (utmMediumFilter) params.utm_medium = utmMediumFilter;
    if (utmContentFilter) params.utm_content = utmContentFilter;
    if (utmTermFilter) params.utm_term = utmTermFilter;
    if (offersFilter?.length) params.offers = offersFilter;
    if (productsFilter?.length) params.products = productsFilter;
    if (affiliatesFilter?.length) params.affiliates = affiliatesFilter;
    if (coproducersFilter?.length) params.coproducers = coproducersFilter;
    if (templatesFilter?.length) params.templates = templatesFilter;

    return normalizeParams(params);
  }, [
    statusFilter,
    paymentMethodsFilter,
    srcFilter,
    utmCampaignFilter,
    utmSourceFilter,
    utmMediumFilter,
    utmContentFilter,
    utmTermFilter,
    offersFilter,
    productsFilter,
    affiliatesFilter,
    coproducersFilter,
    templatesFilter,
  ]);

  const filterVisualParams = useMemo(
    () => convertToVisualParams(filterParams),
    [filterParams]
  );

  const setFilterParams = useCallback(
    (params: FilterParams | ((prev: FilterParams) => FilterParams)) => {
      const newParams =
        typeof params === 'function' ? params(filterParams) : params;

      setStatusFilter((newParams.status as string[]) || null);
      setPaymentMethodsFilter((newParams.payment_methods as string[]) || null);
      setSrcFilter((newParams.src as string) || null);
      setUtmCampaignFilter((newParams.utm_campaign as string) || null);
      setUtmSourceFilter((newParams.utm_source as string) || null);
      setUtmMediumFilter((newParams.utm_medium as string) || null);
      setUtmContentFilter((newParams.utm_content as string) || null);
      setUtmTermFilter((newParams.utm_term as string) || null);
      setOffersFilter((newParams.offers as string[]) || null);
      setProductsFilter((newParams.products as string[]) || null);
      setAffiliatesFilter((newParams.affiliates as string[]) || null);
      setCoproducersFilter((newParams.coproducers as string[]) || null);
      setTemplatesFilter((newParams.templates as string[]) || null);
    },
    [
      filterParams,
      setStatusFilter,
      setPaymentMethodsFilter,
      setSrcFilter,
      setUtmCampaignFilter,
      setUtmSourceFilter,
      setUtmMediumFilter,
      setUtmContentFilter,
      setUtmTermFilter,
      setOffersFilter,
      setProductsFilter,
      setAffiliatesFilter,
      setCoproducersFilter,
      setTemplatesFilter,
    ]
  );

  const fetchPaidSales = useCallback(async () => {
    const defaultDate = getDefaultDate();
    const statusToFetch = statusFilter?.length ? statusFilter : ALL_STATUS;
    return ReportsService.getReportsSalesByStatus({
      startDate: formatDateForAPI(fromDate ?? defaultDate),
      endDate: formatDateForAPI(toDate ?? defaultDate),
      filterParams: {
        ...(convertToSellsFilterParams(filterParams) || {}),
        status: statusToFetch,
      },
      currency,
    });
  }, [fromDate, toDate, filterParams, statusFilter, currency]);

  const fetchHourlySales = useCallback(async () => {
    const defaultDate = getDefaultDate();
    const statusToFetch = statusFilter?.length ? statusFilter : ALL_STATUS;
    return ReportsService.getReports({
      startDate: formatDateForAPI(fromDate ?? defaultDate),
      endDate: formatDateForAPI(toDate ?? defaultDate),
      filterParams: {
        ...(convertToSellsFilterParams(filterParams) || {}),
        status: statusToFetch,
      },
      currency,
    });
  }, [fromDate, toDate, filterParams, statusFilter, currency]);

  const {
    data: sales,
    isPending: isLoading,
    isRefetching,
  } = useQuery({
    queryKey: ['sales-by-status', fromDate, toDate, filterParams, currency],
    queryFn: fetchPaidSales,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
  });

  const {
    data: hourlySalesData,
    isPending: isLoadingHourly,
    isRefetching: isRefetchingHourly,
  } = useQuery({
    queryKey: ['hourly-sales-status', fromDate, toDate, filterParams, currency],
    queryFn: fetchHourlySales,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const transformedHourlySales = useMemo(() => {
    if (!hourlySalesData?.data) return [];
    return hourlySalesData.data.map((item: any) => ({
      hour: item.date_formatted,
      paid: item.paid,
      paid_formatted: item.paid_formatted,
      awaiting_payment: item.awaiting_payment,
      awaiting_payment_formatted: item.awaiting_payment_formatted,
      not_authorized: item.not_authorized,
      not_authorized_formatted: item.not_authorized_formatted,
      chargeback: item.chargeback,
      chargeback_formatted: item.chargeback_formatted,
      refunded: item.refunded,
      refunded_formatted: item.refunded_formatted,
    }));
  }, [hourlySalesData]);

  const activeStatus = useMemo(() => {
    return statusFilter?.length ? statusFilter : ALL_STATUS;
  }, [statusFilter]);

  const cards = useMemo(() => {
    const allCards = [
      {
        text: t('sales:paid'),
        value: sales?.data?.paid?.totals?.amount?.total ?? 'R$ 0,00',
        status: 'paid',
        total: sales?.data?.paid?.totals?.count ?? 0,
        isLoading,
      },
      {
        text: t('sales:pending'),
        value:
          sales?.data?.awaiting_payment?.totals?.amount?.total ?? 'R$ 0,00',
        status: 'awaiting_payment',
        total: sales?.data?.awaiting_payment?.totals?.count ?? 0,
        isLoading,
      },
      {
        text: t('refunded'),
        value: sales?.data?.refunded?.totals?.amount?.total ?? 'R$ 0,00',
        status: 'refunded',
        total: sales?.data?.refunded?.totals?.count ?? 0,
        isLoading,
      },
      {
        text: t('sales:not_authorized'),
        value:
          sales?.data?.not_authorized?.totals?.amount?.total ?? 'R$ 0,00',
        status: 'not_authorized',
        total: sales?.data?.not_authorized?.totals?.count ?? 0,
        isLoading,
      },
      {
        text: t('chargeback'),
        value: sales?.data?.chargeback?.totals?.amount?.total ?? 'R$ 0,00',
        status: 'chargeback',
        total: sales?.data?.chargeback?.totals?.count ?? 0,
        isLoading,
      },
    ];
    return allCards.filter((card) => activeStatus.includes(card.status as any));
  }, [sales, isLoading, activeStatus, t]);

  const percentageData = useMemo(() => {
    const allPercentageData = [
      {
        text: t('sales:paid'),
        percentage: sales?.data?.paid?.percentage ?? 0,
        status: 'paid',
      },
      {
        text: t('sales:pending'),
        percentage: sales?.data?.awaiting_payment?.percentage ?? 0,
        status: 'awaiting_payment',
      },
      {
        text: t('refunded'),
        percentage: sales?.data?.refunded?.percentage ?? 0,
        status: 'refunded',
      },
      {
        text: t('sales:not_authorized'),
        percentage: sales?.data?.not_authorized?.percentage ?? 0,
        status: 'not_authorized',
      },
      {
        text: t('chargeback'),
        percentage: sales?.data?.chargeback?.percentage ?? 0,
        status: 'chargeback',
      },
    ];
    return allPercentageData.filter((item) =>
      activeStatus.includes(item.status as any)
    );
  }, [sales, activeStatus, t]);

  const chartData = useMemo(() => {
    const allChartData = [
      {
        status: 'paid',
        title: t('sales:paid'),
        value: sales?.data?.paid?.totals?.count ?? 0,
      },
      {
        status: 'refunded',
        title: t('refunded'),
        value: sales?.data?.refunded?.totals?.count ?? 0,
      },
      {
        status: 'awaiting_payment',
        title: t('sales:awaiting_payment'),
        value: sales?.data?.awaiting_payment?.totals?.count ?? 0,
      },
      {
        status: 'not_authorized',
        title: t('sales:not_authorized'),
        value: sales?.data?.not_authorized?.totals?.count ?? 0,
      },
      {
        status: 'chargeback',
        title: t('chargeback'),
        value: sales?.data?.chargeback?.totals?.count ?? 0,
      },
    ];
    return allChartData.filter((item) => activeStatus.includes(item.status as any));
  }, [sales, activeStatus, t]);

  const filterBadgeLabel: FilterBadgeLabel = useMemo(
    () => ({
      paid: t('paid'),
      awaiting_payment: t('pending'),
      not_authorized: t('not_authorized'),
      chargeback: t('common:chargeback'),
      pix: t('pix'),
      credit_card: t('credit_card'),
      bank_slip: t('bank_slip'),
      wallet_picpay: t('common:wallet_picpay'),
      refunded: t('common:refunded'),
      wallet_google_pay: t('wallet_google_pay'),
      wallet_apple_pay: t('common:wallet_apple_pay'),
      paypal: t('common:paypal'),
      venmo: t('common:venmo'),
      paze: t('common:paze'),
    }),
    [t]
  );

  const removeFilterItem = useCallback(
    (key: string, index: number) => {
      const filterSetters = {
        status: setStatusFilter,
        payment_methods: setPaymentMethodsFilter,
        offers: setOffersFilter,
        products: setProductsFilter,
        affiliates: setAffiliatesFilter,
        coproducers: setCoproducersFilter,
        templates: setTemplatesFilter,
      };

      const setter = filterSetters[key as keyof typeof filterSetters];
      if (setter) {
        const currentValues = filterParams[key] as string[];
        const newValues = currentValues?.filter((_, i) => i !== index);
        setter(newValues?.length ? newValues : null);
      }
    },
    [
      filterParams,
      setStatusFilter,
      setPaymentMethodsFilter,
      setOffersFilter,
      setProductsFilter,
      setAffiliatesFilter,
      setCoproducersFilter,
      setTemplatesFilter,
    ]
  );

  const removeFilter = useCallback(
    (key: string) => {
      const filterSetters = {
        status: setStatusFilter,
        payment_methods: setPaymentMethodsFilter,
        src: setSrcFilter,
        utm_campaign: setUtmCampaignFilter,
        utm_source: setUtmSourceFilter,
        utm_medium: setUtmMediumFilter,
        utm_content: setUtmContentFilter,
        utm_term: setUtmTermFilter,
        offers: setOffersFilter,
        products: setProductsFilter,
        affiliates: setAffiliatesFilter,
        coproducers: setCoproducersFilter,
        templates: setTemplatesFilter,
      };

      const setter = filterSetters[key as keyof typeof filterSetters];
      if (setter) {
        setter(null);
      }
    },
    [
      setStatusFilter,
      setPaymentMethodsFilter,
      setSrcFilter,
      setUtmCampaignFilter,
      setUtmSourceFilter,
      setUtmMediumFilter,
      setUtmContentFilter,
      setUtmTermFilter,
      setOffersFilter,
      setProductsFilter,
      setAffiliatesFilter,
      setCoproducersFilter,
      setTemplatesFilter,
    ]
  );

  const clearAllFilters = useCallback(() => {
    setStatusFilter(null);
    setPaymentMethodsFilter(null);
    setSrcFilter(null);
    setUtmCampaignFilter(null);
    setUtmSourceFilter(null);
    setUtmMediumFilter(null);
    setUtmContentFilter(null);
    setUtmTermFilter(null);
    setOffersFilter(null);
    setProductsFilter(null);
    setAffiliatesFilter(null);
    setCoproducersFilter(null);
    setTemplatesFilter(null);

    handleDateRangeChange({
      from: new Date(),
      to: new Date(),
    });

    setFilterParams({});
  }, [
    setStatusFilter,
    setPaymentMethodsFilter,
    setSrcFilter,
    setUtmCampaignFilter,
    setUtmSourceFilter,
    setUtmMediumFilter,
    setUtmContentFilter,
    setUtmTermFilter,
    setOffersFilter,
    setProductsFilter,
    setAffiliatesFilter,
    setCoproducersFilter,
    setTemplatesFilter,
    setFromDate,
    setToDate,
  ]);

  const keyLabel: KeyLabel = useMemo(
    () => ({
      status: t('sales:payment_status'),
      payment_methods: t('payment_method'),
      src: t('sales:src'),
      utm_campaign: t('sales:utm_campaign'),
      utm_source: t('sales:utm_source'),
      utm_medium: t('sales:utm_medium'),
      utm_content: t('sales:utm_content'),
      utm_term: t('sales:utm_term'),
      offers: t('sales:offer'),
      products: t('sales:product'),
      affiliates: t('sales:affiliate'),
      coproducers: t('sales:co_producer'),
      templates: t('reports:template'),
    }),
    [t]
  );

  const MAX_VISIBLE_FILTERS = 15;

  const allFilterBadges = useMemo(() => {
    const badges = [];
    Object.entries(filterVisualParams).forEach(([key, values]) => {
      if (typeof values === 'string' && values === '') return null;

      if (
        Array.isArray(values) &&
        values.length > 0 &&
        typeof values[0] === 'string'
      ) {
        values.forEach((value, index) => {
          badges.push({
            key: `${key}-${index}`,
            group: `${keyLabel[key]}:`,
            label: `${filterBadgeLabel[value] || value}`,
            onClick: () => removeFilterItem(key, index),
          });
        });
      } else if (Array.isArray(values) && values.length > 0) {
        values.forEach((value, index) => {
          badges.push({
            key: `${key}-${index}`,
            group: `${keyLabel[key]}:`,
            label: `${value?.label || value?.value || value}`,
            onClick: () => removeFilterItem(key, index),
          });
        });
      } else if (typeof values === 'string' && values !== '') {
        badges.push({
          key: key,
          group: `${keyLabel[key]}:`,
          label: `${filterBadgeLabel[values] || values}`,
          onClick: () => removeFilter(key),
        });
      }
    });
    return badges;
  }, [
    filterVisualParams,
    keyLabel,
    filterBadgeLabel,
    removeFilterItem,
    removeFilter,
  ]);

  return (
    <LayoutInside
      items={[
        { text: t('common:reports'), link: '/reports' },
        {
          text: t('sales_by_status'),
          link: '/reports/status_sale',
        },
      ]}
    >
      <>
        <Link to='/reports' className='w-fit block mb-6'>
          <Button
            variant='default'
            className='bg-transparent text-colorText hover:bg-transparent p-0'
          >
            <ArrowLeft size={16} />
            {t('common:back')}
          </Button>
        </Link>

        <CustomSheet
          open={modals['filter-sales-status']}
          onClose={() => closeModal('filter-sales-status')}
        >
          <FilterSalesByStatus
            filterValues={filterVisualParams}
            onFilter={(data) => {
              setFilterParams({
                ...data,
                products: data.products?.map(
                  (opt) => `${opt.label}:::${opt.value}`
                ),
                offers: data.offers?.map(
                  (opt) => `${opt.label}:::${opt.value}`
                ),
                affiliates: data.affiliates?.map(
                  (opt) => `${opt.label}:::${opt.value}`
                ),
                coproducers: data.coproducers?.map(
                  (opt) => `${opt.label}:::${opt.value}`
                ),
                templates: data.templates?.map(
                  (opt) => `${opt.label}:::${opt.value}`
                ),
              });
            }}
          />
        </CustomSheet>
        <div className='flex items-center mb-6 justify-between flex-wrap max-[410px] gap-4'>
          <DateRangePicker
            defaultValue={dateRange}
            onChange={handleDateRangeChange}
          />

          <Button
            className='flex md:hidden'
            variant='secondary'
            onClick={() => openModal('filter-sales-status')}
          >
            <ListFilter size={24} />
          </Button>

          <Button
            className='hidden md:flex'
            variant='secondary'
            onClick={() => openModal('filter-sales-status')}
            data-cy='open-filter-status'
          >
            <ListFilter size={24} />
            {t('sales:filter')}
          </Button>
        </div>

        {hasActiveFiltersBeyondDates(filterParams, fromDate, toDate) && (
          <div className='flex flex-wrap gap-2 my-4'>
            {allFilterBadges.slice(0, MAX_VISIBLE_FILTERS).map((badge) => (
              <Badge
                key={badge.key}
                className='flex font-medium min-h-7 items-center hover:opacity-80 hover:bg-[#18181b] gap-4 bg-[#18181b] text-colorText'
              >
                <div className='flex text-colorText items-center gap-1'>
                  <span className='text-muted'>{badge.group}</span>
                  {badge.label}
                </div>
                <button
                  onClick={badge.onClick}
                  className='text-muted cursor-pointer'
                >
                  ✕
                </button>
              </Badge>
            ))}

            {allFilterBadges.length > MAX_VISIBLE_FILTERS && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='cursor-pointer rounded bg-[#27272A] px-1.5 py-0.5 text-xs font-semibold flex items-center justify-center'>
                    +{allFilterBadges.length - MAX_VISIBLE_FILTERS}
                  </div>
                </TooltipTrigger>
                <TooltipContent className='w-80 p-4 bg-[#18181b] text-colorText border border-[#333]'>
                  <h4 className='font-semibold mb-2'>{t('sales:filters')}</h4>
                  <div className='flex flex-col gap-2 max-h-60 overflow-y-auto px-4'>
                    {allFilterBadges.slice(MAX_VISIBLE_FILTERS).map((badge) => (
                      <div
                        key={badge.key}
                        className='flex items-center justify-between text-sm'
                      >
                        <div className='gap-1 flex items-start'>
                          <span className='text-muted'>{badge.group}</span>
                          <span>{badge.label}</span>
                        </div>
                        <button
                          onClick={badge.onClick}
                          className='text-muted hover:text-white ml-2'
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            )}

            <Button
              variant='secondary'
              onClick={clearAllFilters}
              data-cy='clear-filters'
              className='ml-2 h-7 flex items-center gap-4 bg-transparent text-interactive'
            >
              {t('clear_all_filters')}
            </Button>
          </div>
        )}
        <div className='mb-6 space-y-8'>
          <section className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4'>
            {cards.map((card) => (
              <CardCurrency
                key={card.status}
                text={card.text}
                value={card.value}
                status={card.status}
                isLoading={isLoading || isRefetching}
                hasTotal
                total={card.total}
              />
            ))}
          </section>

          <section>
            <div className="bg-[#09090b] border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-colorText mb-4">
                {t('percentage_by_status')}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {percentageData.map((item) => (
                  <div
                    key={item.status}
                    className="flex flex-col items-center justify-center p-4 bg-[#18181b] rounded-lg border border-colorBorder"
                  >
                    <p className="text-xs text-zinc-400 mb-2 text-center">
                      {item.text}
                    </p>
                    <Badge
                      variant="outline"
                      className="text-sm font-bold border-0 px-3 py-1"
                      style={{
                        backgroundColor: `${STATUS_COLORS[item.status]}15`,
                        color: STATUS_COLORS[item.status],
                      }}
                    >
                      {item.percentage.toFixed(2)}%
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <SalesByStatusChart
            dateRange={`${format(fromDate, 'dd/MM/yyyy')} - ${format(
              toDate,
              'dd/MM/yyyy'
            )}`}
            chartData={chartData}
            isLoading={isLoading || isRefetching}
          />

          <HourlySalesChart
            hourlySales={transformedHourlySales}
            isLoadingData={isLoadingHourly || isRefetchingHourly}
            text={`${format(fromDate, 'dd/MM/yyyy')} - ${format(
              toDate,
              'dd/MM/yyyy'
            )}`}
            width="w-full"
            title={t('sales:sales')}
          />
        </div>
      </>
    </LayoutInside>
  );
}
